import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, Field, Icon, Input, Modal, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime } from '../../lib/format';
import { useChannels } from '../automations/AutomationsPage';
import { DocumentsCard } from '../documents/DocumentsCard';
import { CodeReveal } from './CodeReveal';
import {
  GRANT_PURPOSE,
  GrantStatusBadge,
  type OperationDetail,
  OperationStatusBadge,
  recipientOf,
} from './common';
import s from './operations.module.css';

type Event = components['schemas']['OperationEvent'];
type Issued = components['schemas']['AccessGrantIssued'];

export const EVENT_LABEL: Record<string, string> = {
  created: 'Operațiune creată și compartiment rezervat',
  updated: 'Detalii actualizate',
  cancelled: 'Operațiune anulată, rezervare eliberată',
  expired: 'Termenul cheii a expirat',
  'grant.issued': 'Cheie emisă',
  'grant.revoked': 'Cheie revocată',
  'kiosk.validated': 'Cod validat la kiosk',
  'kiosk.consumed': 'Cod folosit, ușă deschisă',
  'deposit.confirmed': 'Depunere confirmată',
  'pickup.confirmed': 'Ridicare confirmată',
  'door.timeout': 'Ușă deschisă peste prag',
  'reminder.due': 'Reamintire trimisă înainte de expirare',
};

export function eventLabel(kind: string): string {
  return EVENT_LABEL[kind] ?? kind;
}

export function OperationDetailPage() {
  const { id = '' } = useParams();
  const { has } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ['operation', id],
    queryFn: () => unwrap(api.GET('/operations/{id}', { params: { path: { id } } })),
  });
  const events = useQuery({
    queryKey: ['operation', id, 'events'],
    queryFn: () => unwrap(api.GET('/operations/{id}/events', { params: { path: { id } } })),
  });
  const [cancelling, setCancelling] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['operation', id] });
    void qc.invalidateQueries({ queryKey: ['operations'] });
    void qc.invalidateQueries({ queryKey: ['count', 'operations'] });
    void qc.invalidateQueries({ queryKey: ['access-grants'] });
  };

  if (!query.data)
    return <p className="muted">{query.error ? errorMessage(query.error) : 'Se încarcă…'}</p>;
  const o = query.data;
  const list = (events.data ?? []).slice().sort((a, b) => b.at.localeCompare(a.at));

  return (
    <>
      <div className="flex" style={{ marginBottom: 6 }}>
        <Link to="/operatiuni" className="flex small">
          <Icon name="chevronLeft" size={14} /> Operațiuni
        </Link>
      </div>
      <div className="flex between wrap" style={{ marginBottom: 16, alignItems: 'flex-start' }}>
        <div>
          <h1>{o.title}</h1>
          <div className="flex wrap muted small" style={{ marginTop: 6 }}>
            <OperationStatusBadge status={o.status} />
            <span className="mono">{o.reference}</span>
            <span>
              · creată {fmtDateTime(o.createdAt)} de {o.createdBy}
            </span>
            <span style={o.status === 'expired' ? { color: 'var(--red)' } : undefined}>
              · termen {fmtDateTime(o.expiresAt)}
            </span>
          </div>
        </div>
        <div className="flex">
          {has('operations') && (o.status === 'pending' || o.status === 'ready') && (
            <Button onClick={() => setEditing(true)}>Editează</Button>
          )}
          {has('keys') && (o.status === 'ready' || o.status === 'expired') && (
            <Button icon="key" onClick={() => setReissuing(true)}>
              Reemite cheie de ridicare
            </Button>
          )}
          {has('operations') && (o.status === 'pending' || o.status === 'ready') && (
            <Button variant="danger" icon="close" onClick={() => setCancelling(true)}>
              Anulează
            </Button>
          )}
        </div>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16, alignItems: 'start' }}
      >
        <div className="stack">
          <Card>
            <h2>Detalii</h2>
            <dl className={s.pairs} style={{ marginTop: 12 }}>
              <dt>Destinatar</dt>
              <dd>{recipientOf(o).name}</dd>
              <dt>Contact</dt>
              <dd>
                {[recipientOf(o).email, recipientOf(o).phone].filter(Boolean).join(' · ') || '—'}
              </dd>
              <dt>Locker</dt>
              <dd>
                <Link to={`/lockere/${o.lockerId}`}>Deschide lockerul</Link>
              </dd>
              <dt>Compartiment</dt>
              <dd>{o.slotLabel}</dd>
              <dt>Note interne</dt>
              <dd>{o.notes || '—'}</dd>
              {o.completedAt && (
                <>
                  <dt>Finalizată</dt>
                  <dd>{fmtDateTime(o.completedAt)}</dd>
                </>
              )}
            </dl>
          </Card>
          <Card>
            <h2>Chei de acces</h2>
            <p className="muted small">
              Codurile nu sunt disponibile după emitere; pentru un cod pierdut se emite o cheie
              nouă.
            </p>
            <div style={{ marginTop: 8 }}>
              {o.grants.length === 0 && <p className="muted small">Nu există chei.</p>}
              {o.grants.map((g) => (
                <div key={g.id} className={s.keyRow}>
                  <Icon name="key" size={18} style={{ color: 'var(--blue)' }} />
                  <div className="grow" style={{ flex: 1 }}>
                    <div>
                      {GRANT_PURPOSE[g.purpose]} ·{' '}
                      <span className={s.mask}>{'•'.repeat(g.codeLength)}</span>
                    </div>
                    <div className="small muted">
                      {fmtDateTime(g.validFrom)} → {fmtDateTime(g.expiresAt)}
                      {g.usedAt && ` · folosită ${fmtDateTime(g.usedAt)}`}
                      {g.revokedAt &&
                        ` · revocată ${fmtDateTime(g.revokedAt)}${g.revokedBy ? ` de ${g.revokedBy}` : ''}`}
                    </div>
                  </div>
                  <GrantStatusBadge status={g.status} />
                </div>
              ))}
            </div>
          </Card>
          <DocumentsCard operation={{ id: o.id, title: o.title, orgId: o.orgId }} />
        </div>
        <Card>
          <h2>Cronologie</h2>
          <div className={s.timeline} style={{ marginTop: 14 }}>
            {events.isLoading && <p className="muted small">Se încarcă…</p>}
            {events.error && <p style={{ color: 'var(--red)' }}>{errorMessage(events.error)}</p>}
            {list.map((e: Event) => (
              <div
                key={e.id}
                className={[
                  s.item,
                  /confirmed/.test(e.kind) && s.good,
                  /cancelled|revoked|expired|timeout/.test(e.kind) && s.bad,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <i />
                <h4>{eventLabel(e.kind)}</h4>
                <p>{e.actorLabel}</p>
                <div className={s.stamp}>{fmtDateTime(e.at)}</div>
              </div>
            ))}
            {!events.isLoading && list.length === 0 && (
              <p className="muted small">Niciun eveniment.</p>
            )}
          </div>
        </Card>
      </div>

      {cancelling && (
        <CancelDialog
          operation={o}
          onClose={() => setCancelling(false)}
          onDone={() => {
            setCancelling(false);
            refresh();
          }}
        />
      )}
      {reissuing && (
        <ReissueDialog
          operation={o}
          onClose={() => setReissuing(false)}
          onIssued={(g) => {
            setReissuing(false);
            setIssued(g);
            refresh();
          }}
        />
      )}
      {editing && (
        <EditDialog
          operation={o}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            refresh();
            toast('Detaliile au fost actualizate.', 'success');
          }}
        />
      )}
      {issued && (
        <CodeReveal
          code={issued.code}
          purpose={issued.purpose}
          expiresAt={issued.expiresAt}
          onClose={() => setIssued(null)}
        />
      )}
    </>
  );
}

function CancelDialog({
  operation: o,
  onClose,
  onDone,
}: {
  operation: OperationDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/operations/{id}/cancel', {
          params: { path: { id: o.id } },
          body: { reason: reason.trim() || undefined },
        }),
      ),
    onSuccess: () => {
      toast(
        'Operațiunea a fost anulată; compartimentul e liber și cheile active au fost revocate.',
        'success',
      );
      onDone();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal
      title="Anulează operațiunea"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button variant="danger" onClick={() => cancel.mutate()} busy={cancel.isPending}>
            Confirmă anularea
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>
          Rezervarea compartimentului <strong>{o.slotLabel}</strong> va fi eliberată, iar toate
          cheile active vor fi revocate.
        </p>
        <Field label="Motiv (opțional)" htmlFor="op-cancel-reason">
          <Input
            id="op-cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
        </Field>
      </div>
    </Modal>
  );
}

function ReissueDialog({
  operation: o,
  onClose,
  onIssued,
}: {
  operation: OperationDetail;
  onClose: () => void;
  onIssued: (g: Issued) => void;
}) {
  const toast = useToast();
  const [hours, setHours] = useState<number | ''>('');
  const [channel, setChannel] = useState<'none' | 'email' | 'sms'>('none');
  const channels = useChannels();
  const r = recipientOf(o);
  const issue = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/operations/{id}/access-grants', {
          params: { path: { id: o.id } },
          body: { purpose: 'pickup', keyHours: hours === '' ? undefined : Number(hours), channel },
        }),
      ),
    onSuccess: (g) => onIssued(g),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal
      title="Reemite cheia de ridicare"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            icon="key"
            onClick={() => issue.mutate()}
            busy={issue.isPending}
          >
            Emite cheia
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="small">
          Cheile de ridicare active anterioare se revocă; codul nou apare o singură dată.
        </p>
        <Field label="Valabilitate (ore)" hint="Gol = regula lockerului" htmlFor="op-reissue-hours">
          <Input
            id="op-reissue-hours"
            type="number"
            min={1}
            max={168}
            value={hours}
            onChange={(e) => setHours(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Field>
        <Field label="Trimite codul destinatarului" htmlFor="op-reissue-channel">
          <Select
            id="op-reissue-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as 'none' | 'email' | 'sms')}
          >
            <option value="none">Nu, îl transmit eu</option>
            <option value="email" disabled={!r.email || channels.data?.emailEnabled === false}>
              Prin e-mail{r.email ? ` (${r.email})` : ' (fără adresă)'}
            </option>
            {channels.data?.smsEnabled && (
              <option value="sms" disabled={!r.phone}>
                Prin SMS{r.phone ? ` (${r.phone})` : ' (fără telefon)'}
              </option>
            )}
          </Select>
        </Field>
      </div>
    </Modal>
  );
}

function EditDialog({
  operation: o,
  onClose,
  onDone,
}: {
  operation: OperationDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(recipientOf(o).name);
  const [email, setEmail] = useState(recipientOf(o).email ?? '');
  const [phone, setPhone] = useState(recipientOf(o).phone ?? '');
  const [notes, setNotes] = useState(o.notes);
  const [contactError, setContactError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/operations/{id}', {
          params: { path: { id: o.id } },
          body: {
            notes,
            recipient: {
              name: name.trim(),
              email: email.trim() || null,
              phone: phone.trim() || null,
            },
          },
        }),
      ),
    onSuccess: onDone,
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const submit = () => {
    if (!email.trim() && !phone.trim()) {
      setContactError('E-mail sau telefon, cel puțin unul');
      return;
    }
    setContactError(null);
    save.mutate();
  };
  return (
    <Modal
      title="Editează operațiunea"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={submit}
            busy={save.isPending}
            disabled={name.trim().length < 2}
          >
            Salvează
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Destinatar" htmlFor="op-edit-name">
          <Input
            id="op-edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </Field>
        <Field label="E-mail" error={contactError ?? undefined} htmlFor="op-edit-email">
          <Input
            id="op-edit-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Telefon" htmlFor="op-edit-phone">
          <Input id="op-edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Note interne" htmlFor="op-edit-notes">
          <textarea
            id="op-edit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            style={{
              width: '100%',
              border: '1px solid var(--line)',
              borderRadius: 9,
              padding: 10,
              font: 'inherit',
            }}
          />
        </Field>
      </div>
    </Modal>
  );
}
